import { Box, Button, Flex, Text, Spacer } from '@chakra-ui/react'
import moment from 'moment'
import React, { useCallback, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useDispatch } from 'react-redux'

//UI
import DownloadButton from '@/components/Elements/Button/DownloadButton'
import { Input, Select } from '@/components/Form'
import { formatOffer } from '@/features/offers/helpers/formatOffer'

//Helper

//Constant
import ViewFileButton from '@/components/Elements/Button/ViewFileButton'
import { showVendorQuotationSelectionModal } from '@/features/offers/slices/offerModal.slice'
import useVendor from '@/features/vendors/useVendor'
import { toast } from 'react-toastify'
import { showModal } from '@/stores/ui/modalSlice'
import { MODAL_TYPES } from '@/utils/constants/modalTypes'
import { deepCopy } from '@/utils/objectHelper'
import { formatCurrencyAmount } from '@/utils/stringHelper'
import { Currency } from '@eapproval/offer-shared'

/**
 *
 * This is a component that is used to display the Vendor + Quotation selection for PM040 ,PM100 and PR
 */

const CostBudgetVendorSelection = ({
  costBudgetIndex,
  selectionPath = '',
  selectionLabel = '',
  canSelectQuotation = false,
  canSelectionVendor = false,
  canBeReference = false,
  hasRemark = false,
  shouldSetQuotationDetails = false,
}) => {
  const dispatch = useDispatch()
  const { getValues, setValue } = useFormContext()
  const { useVendorOptions } = useVendor()
  const { data: vendorOptions } = useVendorOptions({ valueKey: '_id', status: ['active', 'temporary'] })

  const watchedVendorDetails = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.subcontractors` })
  const vendorDetails = useMemo(() => watchedVendorDetails ?? [], [watchedVendorDetails])
  const selectedVendorId = useWatch({
    name: `${selectionPath}.vendor`,
  })
  const selectedVendorHistoryIndex = useWatch({
    name: `${selectionPath}.uploadHistoryIndex`,
  })
  const postedVendorOptions = vendorOptions.filter((opt) =>
    vendorDetails.map((detail) => detail.vendor).includes(opt.value)
  )

  const finalVendorOptions = canBeReference
    ? postedVendorOptions.concat([{ label: 'Reference Only', value: 'reference' }])
    : postedVendorOptions

  const selectedQuotationDetail = useMemo(() => {
    const selectVendor = vendorDetails.find((detail) => detail.vendor === selectedVendorId)
    if (!selectVendor) return null

    if (selectedVendorHistoryIndex >= selectVendor.history.length) return null

    return selectVendor.history[selectedVendorHistoryIndex]
  }, [vendorDetails, selectedVendorId, selectedVendorHistoryIndex])

  const handleOpenHistory = useCallback(() => {
    const vendorDetails = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail.subcontractors`) ?? []

    const selectedVendor = vendorDetails.find((detail) => detail.vendor === selectedVendorId)

    if (!selectedVendor) {
      toast.error('Missing Selected Vendor')
      return
    }

    dispatch(
      showVendorQuotationSelectionModal({
        updatePath: `${selectionPath}`,
        vendorHistory: selectedVendor.history,
        title: `${selectionLabel} quotation selection`,
        selectedIndex: selectedVendorHistoryIndex,
        shouldSetQuotationDetails: shouldSetQuotationDetails,
      })
    )
  }, [costBudgetIndex, selectedVendorId, selectedVendorHistoryIndex, dispatch, getValues])

  const handleOpenVendorDetail = () => {
    const vendorIndex = vendorDetails.findIndex((detail) => detail.vendor === selectedVendorId)
    const costBudgetId = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail._id`)
    const isCostBudgetActive = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail.isActive`)
    const vendorDetail = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail.subcontractors.${vendorIndex}`)
    const costBudgetType = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail.type`)
    const RFQCode = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail.RFQCode`)
    const projectName = getValues('projectName')
    const managerId = getValues('manager')
    const offerId = getValues('_id')
    const currentSourcing = getValues('sourcing')
    const formattedOffer = formatOffer({ sourcing: currentSourcing })

    if (vendorIndex < 0 || !vendorDetail?.vendor) {
      toast.error('Missing Selected Vendor')
      return
    }

    if (!costBudgetId) {
      toast.error(`Cost Budget ${costBudgetIndex + 1}: Save the cost budget to open the detail`)
      return
    }

    dispatch(
      showModal({
        modalType: MODAL_TYPES.OFFER_VENDOR_DETAIL,
        modalProps: {
          costBudgetId: costBudgetId,
          costBudgetIndex: costBudgetIndex,
          vendorIndex: vendorIndex,
          vendorDetail: deepCopy(vendorDetail),
          offerDetail: formattedOffer,
          isCostBudgetActive: isCostBudgetActive,
          costBudgetType,
          RFQCode,
          projectName,
          managerId,
          offerId,
        },
      })
    )
  }

  return (
    <Flex w="100%" gap="3" alignItems={'center'}>
      <Flex w="50%">
        <Select
          name={`${selectionPath}.vendor`}
          label={selectionLabel}
          options={finalVendorOptions}
          isRow
          disabled={!canSelectQuotation}
          isRequired={canSelectQuotation}
          extraOnChange={() => {
            setValue(`${selectionPath}.uploadHistoryIndex`, -1, {
              shouldDirty: true,
            })
            if (shouldSetQuotationDetails) {
              setValue(`${selectionPath}.quotationDetails`, [], { shouldDirty: true })
              setValue(`${selectionPath}.discountAmount`, 0, { shouldDirty: true })
              setValue(`${selectionPath}.currency`, 'HKD', { shouldDirty: true })
            }
          }}
          isClearable={false}
        />
      </Flex>
      <Flex direction="column" gap="2">
        {selectedVendorId && selectedVendorId !== 'reference' && canSelectQuotation && (
          <Button colorScheme="gray" onClick={() => handleOpenHistory()}>
            Select Quotation
          </Button>
        )}
        {selectedVendorId && selectedVendorId !== 'reference' && (
          <Button colorScheme="blue" onClick={() => handleOpenVendorDetail()}>
            View Vendor History
          </Button>
        )}
      </Flex>

      {selectedQuotationDetail && (
        <Text>
          {`${formatCurrencyAmount(selectedQuotationDetail?.budget, selectedQuotationDetail?.currency ?? Currency.HKD)}, uploaded at ${moment(selectedQuotationDetail?.submissionDate).format('DD-MMM-YYYY HH:mm:ss')}, ${selectedQuotationDetail.tenderDocumentIndex === 0 ? 'Tender' : `Addendum ${selectedQuotationDetail.tenderDocumentIndex}`}`}
        </Text>
      )}

      <Flex direction="column" gap="2" w="30%">
        {selectedQuotationDetail?.files.length > 0 &&
          selectedQuotationDetail.files.map((file) => (
            <Flex key={file?._id} gap="2" alignItems={'center'}>
              <Text>{file?.originalFileName}</Text>
              <Spacer />

              <ViewFileButton fileId={file?._id} />
              <DownloadButton fileId={file?._id} fileName={file?.originalFileName} />
            </Flex>
          ))}
      </Flex>
      {hasRemark && (
        <Box w="30%">
          <Input
            name={`${selectionPath}.remark`}
            disabled={!canSelectQuotation || !canSelectionVendor}
            isRequired={false}
            placeholder="Remark"
          />
        </Box>
      )}
    </Flex>
  )
}

export default CostBudgetVendorSelection
